import type {
  AriaRole,
  ComponentProps,
  JSX,
  JSXElementConstructor,
  ReactNode,
} from 'react'
import { getClassFn, getHatchedClassFn, type TextClasses } from '#utils/ds.ts'
import { twm } from '#utils/twm.ts'

const classFn = getClassFn<TextClasses>()

// This comes with an escape hatch, and the Text comp uses it, because other
// components are based on the Text component which need to add classes, and
// this provides a second parameter which takes any string.
const hatchedClassFn = getHatchedClassFn<TextClasses>()

// biome-ignore format: keep the matrix
const inlineEls = [
  'abbr', 'b', 'cite', 'code', 'em', 'i', 'kbd', 'q',
  'small', 'span', 'strong', 'sub', 'sup', 'time',
] as const

// A custom `ComponentProps` which omits `className`
export type CompProps<
  T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
> = Omit<ComponentProps<T>, 'className'>
export interface BaseProps extends Omit<TextProps, 'as' | 'className'> {
  className?: (fn: typeof classFn) => string
}
export interface TextProps {
  as?: keyof JSX.IntrinsicElements
  children: ReactNode
  className?: (fn: typeof hatchedClassFn) => string
  hidden?: boolean
  id?: string
  role?: AriaRole
  title?: string
}

export function Text({
  as: As = 'span',
  children,
  className,
  ...props
}: TextProps) {
  const classes = className ? className(hatchedClassFn) : undefined
  const inline = inlineEls.includes(As) ? ' inline-block' : ''

  return (
    <As {...props} className={twm(`capsize font-sans${inline}`, classes)}>
      {children}
    </As>
  )
}
